import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { map } from 'rxjs';
import { TelematicsSectionMapComponent } from '../telematics-section-map/telematics-section-map.component';
import {
  DriversList,
  DriversListData,
} from '../../../shared/interfaces/driver-data/driver-list.interface';
import {
  Ride,
  RideList,
  RideDetail,
  RidesListData,
} from '../../../shared/interfaces/ride-data.interface';
import { DriversService } from '../../../shared/services/drivers.service';
import { RidesService } from '../../../shared/services/rides.service';

import { GoogleMapsModule } from '@angular/google-maps';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { Select } from 'primeng/select';
import { TelematicsSectionDetailsComponent } from '../telematics-section-details/telematics-section-details.component';
import { CollapsibleDirective } from '../../../core/components/pages-container/directives/collapsibleDirective.directive';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { AdjustDropdownHeightDirective } from '../../../shared/directives/adjust-dropdown-height.directive';
import { formatDate } from '../../../shared/utilities/utility-functions';

@Component({
  selector: 'app-telematics-map',
  templateUrl: './telematics-map.component.html',
  styleUrls: ['./telematics-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    TelematicsSectionMapComponent,
    GoogleMapsModule,
    Select,
    DatePicker,
    ButtonModule,
    TelematicsSectionDetailsComponent,
    ReactiveFormsModule,
    CollapsibleDirective,
    ButtonComponent,
    AdjustDropdownHeightDirective,
  ],
})
export class TelematicsMapComponent implements OnInit, OnChanges {
  @Input() rideData: any;
  @ViewChild('TelematicsSectionMap')
  telematicsSectionMap!: TelematicsSectionMapComponent;

  // Map data properties
  public mapData: any = null;
  public mapMarkers: any[] = [];
  public mapOptions: any = null;

  defaultMapOptions = {
    //USA
    center: {
      lat: 37.09024,
      lng: -95.712891,
    },
    zoom: 2.84,
    mapTypeId: google.maps.MapTypeId.TERRAIN,
  };
  public readonly currentDate: Date = new Date();
  public dateOfService?: string;
  public driversList: DriversList[] = [];
  public rideList: RideList[] = [];
  public rideDetailsResult: RideDetail = new RideDetail();
  private isLoadingRideDetails: boolean = false;

  // Filter visibility toggle functionality (like st-grid-filters)
  public showFilters: WritableSignal<boolean> = signal(false);
  public formClosed = signal(false);

  // View switcher for mobile/tablet (map vs details)
  public currentView: 'map' | 'details' = 'map';

  icons = [
    { typeId: BigInt(1), icon: 'assets/images/icons/map-phone.png' }, //Phone Used
    { typeId: BigInt(2), icon: 'assets/images/icons/map-acceleration.png' }, //Acceleration
    { typeId: BigInt(3), icon: 'assets/images/icons/map-speed-limit.png' }, //Speed Limit
    { typeId: BigInt(4), icon: 'assets/images/icons/map-hard-braking.png' }, //Hard Braking
    { typeId: BigInt(5), icon: 'assets/images/icons/map-hard-return.png' }, //Hard Return
  ];

  telematicsForm = this._formBuilder.nonNullable.group({
    dateOfService: [null as unknown as Date, [Validators.required]],
    rideId: [null as unknown as bigint, [Validators.required]],
    driverId: [null as unknown as bigint, [Validators.required]],
  });

  //#region Constructor and ngOnInit
  constructor(
    private readonly _ridesService: RidesService,
    private readonly _driversService: DriversService,
    private readonly _formBuilder: FormBuilder,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    // Always start with a clean state
    this.isLoadingRideDetails = false;
    this.rideList = [];
    this.driversList = [];
    this.rideDetailsResult = new RideDetail();
    this.dateOfService = '';

    let dataToFilter: any = this.rideData;
    if (this.rideData && Object.keys(this.rideData).length > 0) {
      this.dateOfService = formatDate(this.rideData.dateOfService);
      this.telematicsForm.setValue({
        dateOfService: new Date(this.rideData.dateOfService),
        rideId: this.rideData.rideId,
        driverId: this.rideData.driverId,
      });
    } else if (this.telematicsForm.valid) {
      dataToFilter = this.telematicsForm.getRawValue();
    } else {
      this.getDriverList(BigInt(0));
    }
    if (dataToFilter && Object.keys(dataToFilter).length > 0) {
      this.getRideDetails(dataToFilter);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle when rideData input changes from parent component
    if (changes['rideData']) {
      const newRideData = changes['rideData'].currentValue;

      if (newRideData && Object.keys(newRideData).length > 0) {
        // Set the dateOfService directly as string
        this.dateOfService = formatDate(newRideData.dateOfService);

        // Update form with the new ride data
        this.telematicsForm.setValue({
          dateOfService: new Date(newRideData.dateOfService),
          rideId: newRideData.rideId,
          driverId: newRideData.driverId,
        });

        // Load the ride details and display on map
        this.getRideDetails(newRideData);
      } else {
        // Clear data when tab is deactivated (rideData becomes empty)
        this.clearAllData();
      }
    }
  }

  public onDateChange(detectedDate: Date): void {
    const date = detectedDate ? detectedDate.toLocaleDateString('en-CA') : '';
    this.dateOfService = date;
    if (date) {
      this.telematicsForm.controls.dateOfService.setValue(
        this.createDateAsUTC(detectedDate),
      );
    }
    this.telematicsForm.get('rideId')?.reset();
    this.telematicsForm.get('driverId')?.reset();
  }

  public Search(): void {
    if (this.telematicsForm.valid) {
      let dataToFilter = this.telematicsForm.getRawValue();
      this.getRideDetails(dataToFilter as unknown as Ride);
    }
  }

  public Clear(): void {
    this.clearAllData();
  }

  public toggleFilters(): void {
    this.showFilters.update((x) => !x);
  }

  public setCurrentView(view: 'map' | 'details'): void {
    this.currentView = view;
  }

  public getRideDetails(filter: Ride) {
    // Prevent duplicate calls
    if (this.isLoadingRideDetails) {
      return;
    }

    this.isLoadingRideDetails = true;

    // Format the date properly for the API (YYYY-MM-DD string format)
    const dateStr =
      filter.dateOfService instanceof Date
        ? formatDate(filter.dateOfService)
        : formatDate(new Date(filter.dateOfService));

    this._ridesService
      .getRideDetails({
        rideId: filter.rideId,
        driverId: filter.driverId,
        dateOfService: dateStr,
      })
      .subscribe({
        next: (response: any) => {
          this.rideDetailsResult = response.data;
          if (response.data) {
            this.rideData.dateOfService = new Date(
              this.rideDetailsResult.dateOfService,
            );
            this.getRideCoordinates(filter);
          }
          // Load driver list first, then trigger ride list loading
          this.getDriverList(filter.driverId);
        },
        error: (error: HttpErrorResponse) => {
          this.isLoadingRideDetails = false;
        },
        complete: () => {
          // Note: isLoadingRideDetails will be reset after getRideCoordinates completes
        },
      });
  }

  public getRideCoordinates(filter: Ride) {
    this._ridesService.getRideCoordinates(filter.rideId).subscribe({
      next: (response: any) => {
        if (response.data) {
          this.mapData = response.data;
          this.mapOptions = {
            center: {
              lat: response.data.startCoordinate.lat,
              lng: response.data.endCoordinate.lng,
            },
            zoom: 11.7,
            mapTypeId: google.maps.MapTypeId.TERRAIN,
          };
          let startLatLngLiteral: google.maps.LatLngLiteral = {
            lat: response.data.startCoordinate.lat,
            lng: response.data.startCoordinate.lng,
          };
          let endLatLngLiteral: google.maps.LatLngLiteral = {
            lat: response.data.endCoordinate.lat,
            lng: response.data.endCoordinate.lng,
          };
          let markers: Array<any> = [];
          for (let item of response.data.rideEventDetailCoordinates) {
            let iconData = this.icons.find((a) => a.typeId == item.eventTypeId);
            let mapLatLngLiteral: google.maps.LatLngLiteral = {
              lat: item.lat,
              lng: item.lng,
            };
            let mapMark = {
              position: mapLatLngLiteral,
              icon: iconData?.icon ?? '',
              time: item.time,
              location: '',
            };
            markers.push(mapMark);
          }
          markers.push(
            {
              position: startLatLngLiteral,
              icon: {
                url: 'assets/images/icons/map-start.png',
                scaledSize: new google.maps.Size(35, 70),
              },
            },
            {
              position: endLatLngLiteral,
              icon: {
                url: 'assets/images/icons/map-end.png',
                scaledSize: new google.maps.Size(35, 70),
              },
              zoom: 10,
            },
          );
          this.mapMarkers = markers;

          // Also update the ViewChild instance for backward compatibility
          if (this.telematicsSectionMap) {
            this.telematicsSectionMap.basicRideCoordinateDto = response.data;
            this.telematicsSectionMap.markers = markers;
            this.telematicsSectionMap.options = this.mapOptions;
            this.telematicsSectionMap.showData = true;
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoadingRideDetails = false;
      },
      complete: () => {
        this.isLoadingRideDetails = false;
      },
    });
  }

  private getDriverList(driverId: bigint): void {
    this._driversService
      .syncDriversList()
      .pipe(map((res: DriversListData) => res.data))
      .subscribe({
        next: (response: DriversList[]) => {
          this.driversList = response;

          // Trigger change detection to update the dropdown display
          this._cdr.detectChanges();

          // Only get rides if we have a valid driver and date
          if (driverId && driverId !== BigInt(0) && this.dateOfService) {
            this.getRideList(driverId);
          }
        },
        error: (error: HttpErrorResponse) => {},
      });
  }

  public getRidesByDriver(driverId: bigint): void {
    this.telematicsForm.get('rideId')?.reset();
    this.getRideList(driverId);
  }

  private getRideList(driverId: bigint): void {
    if (driverId == BigInt(0) || !this.dateOfService) {
      return;
    }

    this._ridesService
      .syncRidesListByDriverAndDate(driverId, this.dateOfService)
      .pipe(map((res: RidesListData) => res.data))
      .subscribe({
        next: (response: RideList[]) => {
          this.rideList = response;

          // Trigger change detection to update the ride dropdown display
          this._cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.rideList = [];
        },
        complete: () => {},
      });
  }

  get isShow(): boolean {
    return Object.keys(this.rideData).length <= 0;
  }

  private createDateAsUTC(date: Date) {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
  }

  private clearAllData(): void {
    // Reset loading flags
    this.isLoadingRideDetails = false;

    // Reset form to initial state
    this.telematicsForm.reset({
      dateOfService: new Date(),
      driverId: undefined,
      rideId: undefined,
    });

    // Clear data arrays
    this.rideList = [];
    this.driversList = [];
    this.rideDetailsResult = new RideDetail();
    this.dateOfService = '';

    // Clear ride data object (this controls isShow getter)
    this.rideData = new Ride();

    // Clear map data and reset to default state
    this.mapData = null;
    this.mapMarkers = [];
    this.mapOptions = null;

    // Clear map data and reset to default state (ViewChild for backward compatibility)
    if (this.telematicsSectionMap) {
      this.telematicsSectionMap.showData = false;
      this.telematicsSectionMap.basicRideCoordinateDto = null;
      this.telematicsSectionMap.markers = [];
    }

    // Trigger change detection to update UI
    this._cdr.detectChanges();
  }
}
